import type { ReactNode } from 'react';
import { parseAmountToMinor, type CurrencyCode } from '@trestly/shared';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { t } from '../i18n';

interface AmountInputProps {
  value: string;
  onChange: (value: string) => void;
  currency: CurrencyCode;
  label?: string;
  autoFocus?: boolean;
  /** Показать ошибку разбора (обычно только после попытки отправки). */
  showError?: boolean;
  /** Справа от суммы: переключатель валюты в одной строке с полем. */
  trailing?: ReactNode;
  className?: string;
}

/**
 * Ввод суммы строкой. Парсинг — только parseAmountToMinor из @trestly/shared,
 * своей арифметики и float здесь нет.
 */
export function AmountInput({
  value,
  onChange,
  currency,
  label = t('components.amount'),
  autoFocus = false,
  showError = false,
  trailing,
  className,
}: AmountInputProps): JSX.Element {
  const error = amountError(value, currency);
  const invalid = showError && error !== null;

  return (
    <div className={cn('mb-3.5', className)}>
      <span className="label-caps mb-1.5 block">{label}</span>
      <div className="flex items-stretch gap-2">
        <Input
          className="num-hero min-h-15 flex-1 text-center text-[30px]"
          // decimal вызывает цифровую клавиатуру с разделителем на iOS.
          inputMode="decimal"
          autoFocus={autoFocus}
          placeholder="0"
          aria-label={label}
          aria-invalid={invalid}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        {trailing}
      </div>
      {invalid ? <span className="mt-1.5 block text-[13px] text-destructive">{error}</span> : null}
    </div>
  );
}

/** null = значение корректно. */
export function amountError(value: string, currency: CurrencyCode): string | null {
  if (value.trim() === '') return t('components.amountRequired');
  try {
    const minor = parseAmountToMinor(value, currency);
    // В БД стоит check (amount_minor > 0) — не даём отправить ноль.
    if (minor <= 0) return t('components.amountPositive');
    return null;
  } catch {
    return t('components.amountInvalid');
  }
}

/** Безопасный разбор: undefined, если ввод не является суммой. */
export function tryParseAmount(value: string, currency: CurrencyCode): number | undefined {
  if (amountError(value, currency) !== null) return undefined;
  return parseAmountToMinor(value, currency);
}
